/**
 * useListSelection — checkbox selection over a filterable list.
 *
 * Selection is intersected with the currently VISIBLE ids on every
 * render, so rows hidden by a search/filter change never stay silently
 * selected — a bulk action only ever touches rows the user can see.
 */

import { useMemo, useState } from 'react';

export interface ListSelection {
  /** Selected ids, limited to currently visible rows. */
  selected: string[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Selects all visible rows, or clears if all are already selected. */
  toggleAll: () => void;
  clear: () => void;
  allSelected: boolean;
}

export function useListSelection(visibleIds: readonly string[]): ListSelection {
  const [ids, setIds] = useState<ReadonlySet<string>>(new Set());

  const visibleSet = useMemo(() => new Set(visibleIds), [visibleIds]);
  const selected = useMemo(() => [...ids].filter((id) => visibleSet.has(id)), [ids, visibleSet]);
  const allSelected = visibleIds.length > 0 && selected.length === visibleIds.length;

  return {
    selected,
    allSelected,
    isSelected: (id) => ids.has(id) && visibleSet.has(id),
    toggle: (id) =>
      setIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    toggleAll: () => setIds(allSelected ? new Set() : new Set(visibleIds)),
    clear: () => setIds(new Set()),
  };
}
